/**
 * Face Landmarks Component
 *
 * Main exportable component for face landmarks visualization.
 */

import { TFJSMediaPipeDetector } from './detection/tfjs-mediapipe';
import { LandmarkSmoother } from './smoothing/smoother';
import {
  createDemoAnimationState,
  resetDemoAnimation,
  pauseDemoAnimation,
  resumeDemoAnimation,
  updateDemoAnimation,
  renderDemoMask,
  renderOvalGuide,
  calculateOverallProgress,
  type DemoAnimationState
} from './masks/demo-mask';
import { CONFIG } from './config';
import type {
  NormalizedLandmarks,
  ComponentPhase,
  AnimationPhase,
  FaceState,
  FaceLandmarksEvents
} from './types';

type EventHandler<T> = (arg: T) => void;

export class FaceLandmarksComponent {
  // DOM elements
  private container: HTMLElement;
  private wrapper: HTMLDivElement;
  private videoElement: HTMLVideoElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  // Detection
  private detector: TFJSMediaPipeDetector;
  private smoother: LandmarkSmoother;
  private isRunning = false;
  private animationFrameId: number | null = null;

  // State
  private _phase: ComponentPhase = 'idle';
  private animationState: DemoAnimationState;
  private faceState: FaceState = 'lost';
  private lastLandmarks: NormalizedLandmarks | null = null;

  // Screenshot capture
  private screenshotCallback: ((result: { withMesh: string; withoutMesh: string }) => void) | null = null;

  // Stability tracking
  private rawLandmarksHistory: NormalizedLandmarks[] = [];

  // Events
  private eventHandlers: {
    onPhaseChange: EventHandler<ComponentPhase>[];
    onAnimationPhaseChange: EventHandler<[AnimationPhase, number]>[];
    onFaceStateChange: EventHandler<FaceState>[];
    onProgress: EventHandler<number>[];
    onComplete: EventHandler<void>[];
    onError: EventHandler<Error>[];
  } = {
    onPhaseChange: [],
    onAnimationPhaseChange: [],
    onFaceStateChange: [],
    onProgress: [],
    onComplete: [],
    onError: []
  };

  constructor(container: HTMLElement) {
    this.container = container;

    // Create wrapper - fills container vertically
    this.wrapper = document.createElement('div');
    // this.wrapper.style.cssText = 'position:relative;display:inline-block;width:100%;';
   this.wrapper.style.cssText = 'position:relative;width:100%;height:100%;overflow:hidden;';
   this.wrapper.classList.add('camera-wrapper');

    // Create video element - fills container, object-fit:cover for vertical fill
    this.videoElement = document.createElement('video');
    this.videoElement.autoplay = true;
    this.videoElement.playsInline = true;
    this.videoElement.muted = true;
    // this.videoElement.style.cssText = 'display:block;width:100%;height:auto;transform:scaleX(-1) !important;';
   this.videoElement.style.cssText = 'display:block;width:100%;height:100%;object-fit:cover;transform:scaleX(-1) !important;';

    // Create canvas overlay - matches video size and scaling (object-fit:cover to match video)
    this.canvas = document.createElement('canvas');
    this.canvas.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;pointer-events:none;transform:scaleX(-1) !important;';

    // Append elements
    this.wrapper.appendChild(this.videoElement);
    this.wrapper.appendChild(this.canvas);
    this.container.appendChild(this.wrapper);

    // Get canvas context
    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }
    this.ctx = ctx;

    // Initialize detector and smoother
    this.detector = new TFJSMediaPipeDetector();
    this.smoother = new LandmarkSmoother(CONFIG.smooth.factor);
    this.animationState = createDemoAnimationState();
  }

  // Event methods
  on<K extends keyof FaceLandmarksEvents>(
    event: K,
    handler: FaceLandmarksEvents[K]
  ): void {
    if (event === 'onAnimationPhaseChange') {
      this.eventHandlers.onAnimationPhaseChange.push(
        handler as EventHandler<[AnimationPhase, number]>
      );
    } else if (event === 'onComplete') {
      this.eventHandlers.onComplete.push(handler as EventHandler<void>);
    } else if (event === 'onPhaseChange') {
      this.eventHandlers.onPhaseChange.push(handler as EventHandler<ComponentPhase>);
    } else if (event === 'onFaceStateChange') {
      this.eventHandlers.onFaceStateChange.push(handler as EventHandler<FaceState>);
    } else if (event === 'onProgress') {
      this.eventHandlers.onProgress.push(handler as EventHandler<number>);
    } else if (event === 'onError') {
      this.eventHandlers.onError.push(handler as EventHandler<Error>);
    }
  }

  off<K extends keyof FaceLandmarksEvents>(
    event: K,
    handler?: FaceLandmarksEvents[K]
  ): void {
    if (!handler) {
      // Remove all handlers for this event
      if (event in this.eventHandlers) {
        (this.eventHandlers as Record<string, unknown[]>)[event] = [];
      }
    } else {
      // Remove specific handler
      const handlers = this.eventHandlers[event as keyof typeof this.eventHandlers];
      if (handlers) {
        const index = handlers.indexOf(handler as never);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      }
    }
  }

  private emit<K extends keyof typeof this.eventHandlers>(
    event: K,
    ...args: Parameters<(typeof this.eventHandlers)[K][number]>
  ): void {
    const handlers = this.eventHandlers[event];
    for (const handler of handlers) {
      try {
        (handler as (...args: unknown[]) => void)(...args);
      } catch (error) {
        console.error(`Error in ${event} handler:`, error);
      }
    }
  }

  private setPhase(phase: ComponentPhase): void {
    if (this._phase !== phase) {
      this._phase = phase;
      this.emit('onPhaseChange', phase);
    }
  }

  // Lifecycle methods
  async preload(): Promise<void> {
    try {
      this.setPhase('preloading');
      await this.detector.initialize();
      this.setPhase('preloaded');
    } catch (error) {
      this.setPhase('error');
      this.emit('onError', error as Error);
      throw error;
    }
  }

  async init(): Promise<void> {
    try {
      this.setPhase('initializing');

      // Request camera access with fallback for device not found
      let stream: MediaStream;

      try {
        // First try with specific constraints
        const constraints: MediaStreamConstraints = {
          video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        };
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (firstError) {
        // If device not found, try with minimal constraints
        if ((firstError as DOMException).name === 'NotFoundError') {
          console.warn('Camera not found with initial constraints, trying minimal constraints...');
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        } else {
          throw firstError;
        }
      }

      this.videoElement.srcObject = stream;

      // Wait for video to be ready
      await new Promise<void>((resolve) => {
        this.videoElement.onloadedmetadata = () => {
          this.videoElement.play();
          resolve();
        };
      });

      // Set canvas size to match video
      this.updateCanvasSize();

      // Handle resize
      window.addEventListener('resize', () => this.updateCanvasSize());

      this.setPhase('initialized');
    } catch (error) {
      this.setPhase('error');
      this.emit('onError', error as Error);
      throw error;
    }
  }

  start(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.animationState.phase = 'waiting_for_face';
    this.setPhase('running');
    this.runDetectionLoop();
  }

  stop(): void {
    this.isRunning = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  dispose(): void {
    this.stop();

    if (this.detector) {
      this.detector.dispose();
    }

    if (this.videoElement.srcObject) {
      const stream = this.videoElement.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
    }

    this.container.removeChild(this.wrapper);
  }

  // Screenshot - captures video with optional mask overlay
  private takeScreenshotInternal(includeMask: boolean): string {
    const offscreen = document.createElement('canvas');
    offscreen.width = this.canvas.width;
    offscreen.height = this.canvas.height;
    const ctx = offscreen.getContext('2d')!;

    // Draw video (mirrored)
    ctx.save();
    ctx.translate(offscreen.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(this.videoElement, 0, 0);
    ctx.restore();

    // Draw mask (mirrored) if requested
    if (includeMask) {
      ctx.save();
      ctx.translate(offscreen.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(this.canvas, 0, 0);
      ctx.restore();
    }

    return offscreen.toDataURL('image/png');
  }

  // Public method for taking screenshot with mask
  takeScreenshot(): string {
    return this.takeScreenshotInternal(true);
  }

  /**
   * Request screenshots to be captured at the end of Phase 2 (lines).
   * Returns a promise that resolves with both screenshot variants:
   * - withMesh: screenshot with the face mesh overlay
   * - withoutMesh: clean screenshot without mesh
   */
  getScreenshotWhenReady(): Promise<{ withMesh: string; withoutMesh: string }> {
    return new Promise((resolve) => {
      this.screenshotCallback = resolve;
    });
  }

  // Getters
  get phase(): ComponentPhase {
    return this._phase;
  }

  get animationPhase(): AnimationPhase {
    return this.animationState.phase;
  }

  get progress(): number {
    return calculateOverallProgress(this.animationState);
  }

  get isComplete(): boolean {
    return this.animationState.phase === 'complete';
  }

  // Private methods
  private updateCanvasSize(): void {
    const { videoWidth, videoHeight } = this.videoElement;
    if (videoWidth && videoHeight) {
      this.canvas.width = videoWidth;
      this.canvas.height = videoHeight;
    }
  }

  private runDetectionLoop = async (): Promise<void> => {
    if (!this.isRunning) return;

    if (this.videoElement.readyState !== this.videoElement.HAVE_ENOUGH_DATA) {
      this.animationFrameId = requestAnimationFrame(this.runDetectionLoop);
      return;
    }

    let landmarks: NormalizedLandmarks | null = null;
    try {
      landmarks = await this.detector.detect(this.videoElement);
    } catch (error) {
      console.error('Detection error:', error);
    }

    this.handleDetection(landmarks);

    this.animationFrameId = requestAnimationFrame(this.runDetectionLoop);
  };

  private handleDetection(rawLandmarks: NormalizedLandmarks | null): void {
    this.updateStabilityHistory(rawLandmarks);

    let landmarks = rawLandmarks;
    if (landmarks) {
      landmarks = this.smoother.smooth(landmarks);
    }

    const newFaceState = this.checkFaceState(landmarks);
    if (newFaceState !== this.faceState) {
      this.faceState = newFaceState;
      this.emit('onFaceStateChange', newFaceState);
    }

    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Update lastLandmarks whenever we have valid landmarks (even if face state is not ok)
    if (landmarks) {
      this.lastLandmarks = landmarks;
    }

    if (this.faceState !== 'ok') {
      this.handleFaceInvalid(landmarks);
      return;
    }

    this.handleFaceValid(landmarks!);
  }

  private updateStabilityHistory(landmarks: NormalizedLandmarks | null): void {
    if (!landmarks || landmarks.points.length === 0) {
      this.rawLandmarksHistory = [];
      return;
    }

    this.rawLandmarksHistory.push(landmarks);

    if (this.rawLandmarksHistory.length > CONFIG.stability.historySize) {
      this.rawLandmarksHistory.shift();
    }
  }

  private isLandmarksStable(): boolean {
    if (this.rawLandmarksHistory.length < 3) {
      return true;
    }

    const keyPoints = [1, 10, 152, 234, 454];
    let totalMovement = 0;
    let measurements = 0;

    for (let i = 1; i < this.rawLandmarksHistory.length; i++) {
      const prev = this.rawLandmarksHistory[i - 1];
      const curr = this.rawLandmarksHistory[i];

      for (const idx of keyPoints) {
        if (idx < prev.points.length && idx < curr.points.length) {
          const dx = curr.points[idx].x - prev.points[idx].x;
          const dy = curr.points[idx].y - prev.points[idx].y;
          totalMovement += Math.sqrt(dx * dx + dy * dy);
          measurements++;
        }
      }
    }

    if (measurements === 0) return true;

    const avgMovement = totalMovement / measurements;
    return avgMovement < CONFIG.stability.threshold;
  }

  private checkFaceState(landmarks: NormalizedLandmarks | null): FaceState {
    if (!landmarks || landmarks.points.length === 0) {
      return 'lost';
    }

    const phase = this.animationState.phase;
    const inAnimationPhase = phase === 'waiting_for_face' || phase === 'dots' || phase === 'lines' || phase === 'fill';

    if (inAnimationPhase) {
      const faceSize = this.calculateFaceSize(landmarks);
      if (faceSize < CONFIG.faceTracking.minFaceSize) {
        return 'too_far';
      }
    }

    if (inAnimationPhase && !this.isLandmarksStable()) {
      return 'unstable';
    }

    return 'ok';
  }

  private calculateFaceSize(landmarks: NormalizedLandmarks): number {
    const points = landmarks.points;
    if (points.length < 10) return 0;

    let minY = Infinity, maxY = -Infinity;
    for (const point of points) {
      if (point.y < minY) minY = point.y;
      if (point.y > maxY) maxY = point.y;
    }

    const faceHeight = maxY - minY;
    const videoHeight = this.canvas.height || 720;

    return faceHeight / videoHeight;
  }

  private handleFaceInvalid(currentLandmarks: NormalizedLandmarks | null): void {
    if (this.animationState.phase !== 'waiting_for_face') {
      pauseDemoAnimation(this.animationState);
    }

    const showWarning = this.animationState.phase !== 'waiting_for_face';
    renderOvalGuide(this.ctx, showWarning);

    // Use current landmarks if available (for face tracking), otherwise fall back to last known
    const landmarksToRender = currentLandmarks || this.lastLandmarks;
    if (landmarksToRender && this.animationState.phase !== 'waiting_for_face') {
      renderDemoMask(this.ctx, landmarksToRender, this.animationState);
    }
  }

  private handleFaceValid(landmarks: NormalizedLandmarks): void {
    if (this.animationState.paused) {
      resumeDemoAnimation(this.animationState);
    }

    if (this.animationState.phase === 'waiting_for_face') {
      resetDemoAnimation(this.animationState);
    }

    const previousPhase = this.animationState.phase;
    const { overallProgress } = updateDemoAnimation(this.animationState);
    const currentPhase = this.animationState.phase;

    // If transitioning from lines to fill and screenshot is requested, capture both variants
    if (previousPhase === 'lines' && currentPhase === 'fill' && this.screenshotCallback) {
      // Temporarily set back to lines to render the completed lines state
      this.animationState.phase = 'lines';
      // Render without progress text for screenshot
      renderDemoMask(this.ctx, landmarks, this.animationState, true);
      // Capture screenshot with mesh
      const withMesh = this.takeScreenshotInternal(true);
      // Capture screenshot without mesh
      const withoutMesh = this.takeScreenshotInternal(false);
      // Resolve the promise with both screenshots
      this.screenshotCallback({ withMesh, withoutMesh });
      this.screenshotCallback = null;
      // Restore to fill phase for subsequent frames
      this.animationState.phase = 'fill';
    } else {
      // Render the mask normally
      renderDemoMask(this.ctx, landmarks, this.animationState);
    }

    // Detect phase transitions
    if (currentPhase !== previousPhase) {
      this.emit('onAnimationPhaseChange', [currentPhase, overallProgress]);
    }

    // Emit progress
    this.emit('onProgress', overallProgress);

    if (this.animationState.phase === 'complete' && this._phase !== 'complete') {
      this.setPhase('complete');
      this.emit('onComplete', undefined);
    }
  }
}
